import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  HelpCircle,
  Clock,
  Calendar,
  AlertTriangle,
  Send,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Award,
  RotateCcw,
  ShieldAlert,
  MapPin,
  Cloud,
  WifiOff,
} from 'lucide-react';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { SatelliteMapPicker } from '../components/SatelliteMapPicker';

export const StudentExamPortal: React.FC = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [loading, setLoading] = useState<boolean>(true);
  const [exams, setExams] = useState<any[]>([]);

  // Exam Taking Session States
  const [activeSession, setActiveSession] = useState<any>(null); // { attempt_id, exam_set, questions, remaining_seconds, started_at }
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Result & Review States
  const [reviewResult, setReviewResult] = useState<any>(null); // { attempt, questions }
  const [reviewModalOpen, setReviewModalOpen] = useState<boolean>(false);

  // Anti-Cheating & Proctoring States
  const [violationAlert, setViolationAlert] = useState<{ open: boolean; count: number; max: number } | null>(null);

  // Auto-Save & Recovery States
  const [saveStatus, setSaveStatus] = useState<'IDLE' | 'SAVING' | 'SAVED' | 'OFFLINE'>('IDLE');
  const [lastSavedTime, setLastSavedTime] = useState<string>('');
  const [resumedAlert, setResumedAlert] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const answersRef = useRef<Record<string, string>>({});

  // GPS Geofence Verification Dialog State
  const [geofenceModal, setGeofenceModal] = useState<{
    open: boolean;
    message: string;
    exam: any;
    studentCoords?: { latitude: number; longitude: number };
  } | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch student's available exams
  const fetchMyExams = async () => {
    try {
      setLoading(true);
      const res = await api.get('/exams/my-exams');
      setExams(res.data || []);
    } catch (err) {
      console.error('Failed to load my exams:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyExams();
  }, []);

  // Resilient two-stage GPS position acquisition (High accuracy satellite -> Network/Wi-Fi fallback)
  const getResilientPosition = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        (err) => {
          console.warn('High accuracy GPS timed out or failed, falling back to network geolocation:', err?.message);
          navigator.geolocation.getCurrentPosition(
            (fallbackPos) => resolve(fallbackPos),
            (fallbackErr) => reject(fallbackErr),
            {
              enableHighAccuracy: false,
              timeout: 15000,
              maximumAge: 180000,
            }
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 6000,
          maximumAge: 60000,
        }
      );
    });
  };

  // Anti-Cheating & Screen Capture Protection: Listen for tab switch, app backgrounding, and screen capture shortcuts
  useEffect(() => {
    if (!activeSession) return;

    let violationLock = false;

    // Detect mobile touch devices (smartphones / tablets)
    const isTouchDevice =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const triggerViolation = async (reason: string) => {
      if (violationLock || submitting || confirmSubmitOpen || Boolean(violationAlert?.open)) return;
      violationLock = true;
      try {
        const res = await api.post(`/exams/my-exams/${activeSession.exam_set.id}/record-violation`, {
          attempt_id: activeSession.attempt_id,
          reason,
          answers,
        });

        const { tab_switch_count, allowed_tab_switches, is_terminated } = res.data;

        if (is_terminated) {
          const finishedSetId = activeSession.exam_set.id;
          setActiveSession(null);
          setConfirmSubmitOpen(false);
          setViolationAlert(null);
          if (timerRef.current) clearInterval(timerRef.current);
          alert(
            `⚠️ คุณทำผิดระเบียบการสอบ: สลับหน้าจอหรือออกจากหน้าต่างข้อสอบเกินจำนวนที่อนุญาต (${tab_switch_count}/${allowed_tab_switches} ครั้ง)\nระบบได้ทำการตัดสิทธิ์และส่งข้อสอบโดยอัตโนมัติแล้ว`
          );
          await handleViewResult(finishedSetId);
          fetchMyExams();
        } else {
          setViolationAlert({
            open: true,
            count: tab_switch_count,
            max: allowed_tab_switches,
          });
        }
      } catch (err) {
        console.error('Failed to record violation:', err);
      } finally {
        setTimeout(() => {
          violationLock = false;
        }, 2000);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden || document.visibilityState === 'hidden') {
        triggerViolation('VISIBILITY_HIDDEN');
      }
    };

    const handleBlur = () => {
      // On mobile / touch devices, do not use window.blur because scrolling or browser address bar collapses trigger false positives
      if (isTouchDevice) return;
      if (!document.hasFocus() || document.hidden) {
        triggerViolation('WINDOW_BLUR');
      }
    };

    const handlePageHide = () => {
      triggerViolation('PAGE_HIDE');
    };

    // Keyboard Screen Capture & DevTools Interceptor
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        e.preventDefault();
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText('');
        }
        alert('⚠️ ระบบไม่อนุญาตให้จับภาพหน้าจอ (Screen Capture Protection) ระหว่างการสอบ');
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        ['p', 'P', 's', 'S', 'c', 'C', 'u', 'U'].includes(e.key)
      ) {
        e.preventDefault();
      }
      if (
        e.key === 'F12' ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && ['I', 'i', 'J', 'j', 'C', 'c'].includes(e.key))
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('keydown', handleKeyDown);

    if (!isTouchDevice) {
      window.addEventListener('blur', handleBlur);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('keydown', handleKeyDown);
      if (!isTouchDevice) {
        window.removeEventListener('blur', handleBlur);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, submitting, confirmSubmitOpen, violationAlert?.open, answers]);

  // Countdown Timer
  useEffect(() => {
    if (activeSession && remainingTime > 0) {
      timerRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            handleAutoSubmitOnTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeSession, remainingTime]);

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Perform Auto-Save Draft to backend
  const performSaveDraft = async (currentAnswers: Record<string, string>, silent = false) => {
    if (!activeSession) return;
    if (!silent) setSaveStatus('SAVING');
    try {
      const res = await api.post(`/exams/my-exams/${activeSession.exam_set.id}/save-draft`, {
        attempt_id: activeSession.attempt_id,
        answers: currentAnswers,
      });
      if (res.data?.success) {
        setSaveStatus('SAVED');
        const now = new Date();
        setLastSavedTime(
          now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        );
      }
    } catch (err: any) {
      if (err.response?.data?.is_timed_out) {
        handleAutoSubmitOnTimeout();
      } else {
        setSaveStatus('OFFLINE');
      }
    }
  };

  // Background Auto-Save sync & Online/Offline listeners
  useEffect(() => {
    if (!activeSession) return;

    const handleOnline = () => {
      if (answersRef.current && Object.keys(answersRef.current).length > 0) {
        performSaveDraft(answersRef.current);
      }
    };

    const handleOffline = () => {
      setSaveStatus('OFFLINE');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic safety sync every 20 seconds
    const periodicInterval = setInterval(() => {
      if (navigator.onLine && answersRef.current && Object.keys(answersRef.current).length > 0) {
        performSaveDraft(answersRef.current, true);
      }
    }, 20000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(periodicInterval);
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);

  // Start Exam Attempt with Geofencing verification & Resume support
  const handleStartExam = async (ex: any, isResume = false) => {
    if (isResume) {
      if (
        !window.confirm(
          'คุณต้องการกลับเข้าสู่หน้าข้อสอบเพื่อทำต่อใช่หรือไม่? (ระบบจะนำคุณกลับเข้าสู่หน้าข้อสอบพร้อมคำตอบที่บันทึกไว้ล่าสุดทันที และนาฬิกาจะนับเวลาต่อจากที่เหลืออยู่)'
        )
      ) {
        return;
      }
    } else {
      if (
        !window.confirm(
          'คุณพร้อมที่จะเริ่มทำข้อสอบแล้วหรือไม่? นาฬิกาจับเวลาจะเริ่มนับถอยหลังทันทีเมื่อเข้าสู่หน้าข้อสอบ และระบบจะตรวจจับการสลับหน้าจอ (Anti-Cheating)'
        )
      ) {
        return;
      }
    }

    let deviceCoords: { device_latitude?: number; device_longitude?: number } = {};

    // Geofencing verification (Only if required AND not an existing resume)
    if (ex.require_geofence && !isResume) {
      if (!navigator.geolocation) {
        alert('อุปกรณ์หรือเบราว์เซอร์ของท่านไม่รองรับการระบุตำแหน่ง GPS ซึ่งจำเป็นสำหรับการสอบนี้');
        return;
      }

      setLoading(true);
      try {
        const position = await getResilientPosition();
        deviceCoords = {
          device_latitude: position.coords.latitude,
          device_longitude: position.coords.longitude,
        };
      } catch (geoErr: any) {
        setLoading(false);
        setGeofenceModal({
          open: true,
          message:
            'ไม่สามารถระบุตำแหน่งพิกัด GPS ได้ กรุณาเปิด Location Services บนอุปกรณ์ของท่าน และอนุญาตให้เบราว์เซอร์เข้าถึงตำแหน่งที่ตั้งก่อนเริ่มทำข้อสอบ',
          exam: ex,
        });
        return;
      }
    }

    try {
      setLoading(true);
      const res = await api.post(`/exams/my-exams/${ex.id}/start`, deviceCoords);
      const sessionData = res.data;
      setActiveSession(sessionData);
      setRemainingTime(sessionData.remaining_seconds);

      // Restore answers: merge server-saved answers with local storage draft if available
      let mergedAnswers = sessionData.saved_answers || {};
      try {
        const localDraft = localStorage.getItem(`tpms_exam_draft_${sessionData.attempt_id}`);
        if (localDraft) {
          const parsed = JSON.parse(localDraft);
          mergedAnswers = { ...mergedAnswers, ...parsed };
        }
      } catch (e) {}

      setAnswers(mergedAnswers);
      answersRef.current = mergedAnswers;
      setCurrentQuestionIndex(0);
      setSaveStatus('SAVED');
      const now = new Date();
      setLastSavedTime(
        now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );

      if (sessionData.is_resumed || isResume) {
        const answeredCount = Object.keys(mergedAnswers).length;
        setResumedAlert(
          `เชื่อมต่อการสอบต่อสำเร็จ! กู้คืนคำตอบที่บันทึกไว้ได้ ${answeredCount} ข้อ และเหลือเวลาสอบอีก ${Math.floor(
            sessionData.remaining_seconds / 60
          )} นาที`
        );
      } else {
        setResumedAlert(null);
      }
    } catch (err: any) {
      if (err.response?.status === 403 && err.response?.data?.distance_meters !== undefined) {
        setGeofenceModal({
          open: true,
          message: err.response.data.message,
          exam: ex,
          studentCoords: {
            latitude: deviceCoords.device_latitude!,
            longitude: deviceCoords.device_longitude!,
          },
        });
      } else {
        alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการเริ่มทำข้อสอบ');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Answer Selection with instant Local Storage backup & Debounced backend auto-save
  const handleSelectAnswer = (questionId: string, optionKey: string) => {
    const updated = {
      ...answers,
      [questionId]: optionKey,
    };
    setAnswers(updated);
    answersRef.current = updated;

    // 1. Immediately backup to browser localStorage (Disaster recovery)
    if (activeSession?.attempt_id) {
      try {
        localStorage.setItem(`tpms_exam_draft_${activeSession.attempt_id}`, JSON.stringify(updated));
      } catch (e) {}
    }

    // 2. Debounced auto-save to backend API (800ms)
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveStatus('SAVING');
    autoSaveTimerRef.current = setTimeout(() => {
      performSaveDraft(updated);
    }, 800);
  };

  // Submit Exam
  const handleSubmitExam = async (isTimeout = false) => {
    if (!activeSession) return;
    const setId = activeSession.exam_set.id;
    const attemptId = activeSession.attempt_id;
    try {
      setSubmitting(true);
      await api.post(`/exams/my-exams/${setId}/submit`, {
        attempt_id: attemptId,
        answers,
        is_timed_out: isTimeout,
      });

      // Clear local storage draft upon successful submission
      try {
        localStorage.removeItem(`tpms_exam_draft_${attemptId}`);
      } catch (e) {}

      setConfirmSubmitOpen(false);
      setActiveSession(null);
      setResumedAlert(null);
      if (timerRef.current) clearInterval(timerRef.current);

      // Fetch review result
      await handleViewResult(setId);
      fetchMyExams();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำตอบ';
      if (err.response?.status === 400 && msg.includes('ถูกส่งคำตอบไปแล้ว')) {
        try {
          localStorage.removeItem(`tpms_exam_draft_${attemptId}`);
        } catch (e) {}
        setConfirmSubmitOpen(false);
        setActiveSession(null);
        setResumedAlert(null);
        if (timerRef.current) clearInterval(timerRef.current);
        alert(msg);
        await handleViewResult(setId);
        fetchMyExams();
      } else {
        alert(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoSubmitOnTimeout = () => {
    alert('หมดเวลาการทำข้อสอบแล้ว! ระบบกำลังบันทึกและส่งคำตอบของคุณโดยอัตโนมัติ');
    handleSubmitExam(true);
  };

  // View Result / Review
  const handleViewResult = async (examSetId: string) => {
    try {
      setLoading(true);
      const res = await api.get(`/exams/my-exams/${examSetId}/result`);
      setReviewResult(res.data);
      setReviewModalOpen(true);
    } catch (err: any) {
      alert(err.response?.data?.message || 'ไม่สามารถโหลดผลการสอบได้');
    } finally {
      setLoading(false);
    }
  };

  // ========================================================
  // RENDER 1: ACTIVE EXAM SESSION (หน้าทำข้อสอบ)
  // ========================================================
  if (activeSession) {
    const currentQuestion = activeSession.questions[currentQuestionIndex];
    const answeredCount = Object.keys(answers).length;
    const totalQuestions = activeSession.questions.length;
    const isWarningTime = remainingTime <= 300; // <= 5 minutes

    return (
      <Box sx={{ pb: { xs: 10, md: 6 } }}>
        {/* Sticky Session Header with Countdown Timer */}
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 75,
            zIndex: 100,
            p: 2,
            mb: 3,
            borderRadius: 2.5,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E3A8A' }}>
              {activeSession.exam_set.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeSession.exam_set.course_code ? `${activeSession.exam_set.course_code} ${activeSession.exam_set.course_name}` : 'แบบทดสอบประมวลผลความรู้'} • ตอบแล้ว {answeredCount} จาก {totalQuestions} ข้อ
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Auto-Save Progress Indicator */}
            {saveStatus === 'SAVING' ? (
              <Chip
                icon={<RotateCcw size={13} className="animate-spin" />}
                label="กำลังบันทึก..."
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.72rem' }}
              />
            ) : saveStatus === 'SAVED' ? (
              <Chip
                icon={<Cloud size={13} color="#059669" />}
                label={lastSavedTime ? `บันทึกอัตโนมัติแล้ว (${lastSavedTime})` : 'บันทึกอัตโนมัติแล้ว'}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.72rem', borderColor: 'rgba(5, 150, 105, 0.4)' }}
              />
            ) : saveStatus === 'OFFLINE' ? (
              <Chip
                icon={<WifiOff size={13} color="#DC2626" />}
                label="บันทึกในเครื่อง (รอเชื่อมเน็ต)"
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.72rem' }}
              />
            ) : null}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2.2,
                py: 1,
                borderRadius: 3,
                bgcolor: isWarningTime ? '#FEF2F2' : '#EFF6FF',
                border: '1px solid',
                borderColor: isWarningTime ? '#FCA5A5' : '#BFDBFE',
                color: isWarningTime ? '#DC2626' : '#1E40AF',
              }}
            >
              <Clock size={20} className={isWarningTime ? 'animate-pulse' : ''} />
              <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
                {formatTimer(remainingTime)}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Send size={18} />}
              onClick={() => setConfirmSubmitOpen(true)}
              sx={{ borderRadius: 2, fontWeight: 700, px: 2.5 }}
            >
              ส่งข้อสอบ
            </Button>
          </Box>
        </Paper>

        {resumedAlert && (
          <Alert
            severity="success"
            onClose={() => setResumedAlert(null)}
            sx={{ mb: 2.5, borderRadius: 2, fontWeight: 600, boxShadow: '0 4px 12px rgba(5, 150, 105, 0.15)' }}
          >
            {resumedAlert}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Question Content (Left Column) */}
          <Grid size={{ xs: 12, md: 8.5 }}>
            <GlassCard
              highlightColor="#1E40AF"
              sx={{
                p: { xs: 2.5, md: 4 },
                position: 'relative',
                overflow: 'hidden',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                return false;
              }}
              onCopy={(e) => {
                e.preventDefault();
                return false;
              }}
            >
              {/* Security Watermark & Screen Capture Deterrent Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                  zIndex: 0,
                  overflow: 'hidden',
                  opacity: 0.045,
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-around',
                  alignContent: 'space-around',
                  userSelect: 'none',
                }}
              >
                {Array.from({ length: 12 }).map((_, wIdx) => (
                  <Box
                    key={wIdx}
                    sx={{
                      transform: 'rotate(-25deg)',
                      fontSize: '0.85rem',
                      fontWeight: 800,
                      color: '#000000',
                      p: 2,
                      textAlign: 'center',
                      lineHeight: 1.3,
                    }}
                  >
                    <div>{user.student_code || user.username || 'TPMS EXAM'}</div>
                    <div>{user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'KPRU'}</div>
                    <div style={{ fontSize: '0.7rem' }}>SECURE EXAM • DO NOT CAPTURE</div>
                  </Box>
                ))}
              </Box>

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={`ข้อที่ ${currentQuestionIndex + 1} จาก ${totalQuestions}`}
                    color="primary"
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    label={`${currentQuestion?.points || 1.0} คะแนน`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Question Text (Rendered HTML from Tiptap) */}
                <Box
                  sx={{
                    fontSize: '1.15rem',
                    lineHeight: 1.7,
                    color: '#0F172A',
                    mb: 3.5,
                    '& p': { mb: 1.5 },
                    '& strong': { color: '#1E3A8A' },
                  }}
                  dangerouslySetInnerHTML={{ __html: currentQuestion?.question_text }}
                />

                {/* Options Radio Cards */}
                <RadioGroup
                  value={answers[currentQuestion?.id] || ''}
                  onChange={(e) => handleSelectAnswer(currentQuestion?.id, e.target.value)}
                >
                  {[
                    { key: 'A', label: 'ก.', text: currentQuestion?.option_a },
                    { key: 'B', label: 'ข.', text: currentQuestion?.option_b },
                    { key: 'C', label: 'ค.', text: currentQuestion?.option_c },
                    { key: 'D', label: 'ง.', text: currentQuestion?.option_d },
                  ].map((opt) => {
                    const isSelected = answers[currentQuestion?.id] === opt.key;
                    return (
                      <Paper
                        key={opt.key}
                        elevation={0}
                        onClick={() => handleSelectAnswer(currentQuestion?.id, opt.key)}
                        sx={{
                          p: 2,
                          mb: 1.8,
                          borderRadius: 2.5,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : '#E2E8F0',
                          bgcolor: isSelected ? 'rgba(30, 64, 175, 0.04)' : '#FFFFFF',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: isSelected ? 'primary.main' : '#94A3B8',
                            bgcolor: isSelected ? 'rgba(30, 64, 175, 0.06)' : '#F8FAFC',
                          },
                        }}
                      >
                        <FormControlLabel
                          value={opt.key}
                          control={<Radio color="primary" />}
                          label={
                            <Typography variant="body1" sx={{ fontWeight: isSelected ? 700 : 500, color: '#1E293B' }}>
                              <strong>{opt.label}</strong> {opt.text}
                            </Typography>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    );
                  })}
                </RadioGroup>

                {/* Question Navigation Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowLeft size={16} />}
                    disabled={currentQuestionIndex === 0}
                    onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
                    sx={{ borderRadius: 2 }}
                  >
                    ข้อก่อนหน้า
                  </Button>

                  {answers[currentQuestion?.id] && (
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<RotateCcw size={14} />}
                      onClick={() => {
                        const updated = { ...answers };
                        delete updated[currentQuestion?.id];
                        setAnswers(updated);
                      }}
                      sx={{ color: '#64748B' }}
                    >
                      ล้างคำตอบข้อนี้
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    endIcon={<ArrowRight size={16} />}
                    disabled={currentQuestionIndex === totalQuestions - 1}
                    onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
                    sx={{ borderRadius: 2 }}
                  >
                    ข้อถัดไป
                  </Button>
                </Box>
              </Box>
            </GlassCard>
          </Grid>

          {/* Question Navigator Palette (Right Column) */}
          <Grid size={{ xs: 12, md: 3.5 }}>
            <GlassCard sx={{ p: 2.5, position: 'sticky', top: 180 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                แผงผังข้อสอบ (Question Navigator)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                คลิกที่หมายเลขข้อเพื่อกระโดดไปยังข้อนั้นๆ
              </Typography>

              <Grid container spacing={1} sx={{ mb: 3 }}>
                {activeSession.questions.map((q: any, i: number) => {
                  const isCur = i === currentQuestionIndex;
                  const isAns = Boolean(answers[q.id]);
                  return (
                    <Grid size={{ xs: 2.4 }} key={q.id}>
                      <Button
                        fullWidth
                        size="small"
                        variant={isCur ? 'contained' : isAns ? 'outlined' : 'text'}
                        color={isCur ? 'primary' : isAns ? 'success' : 'inherit'}
                        onClick={() => setCurrentQuestionIndex(i)}
                        sx={{
                          minWidth: 'auto',
                          py: 1,
                          fontWeight: 700,
                          borderRadius: 2,
                          bgcolor: isCur ? 'primary.main' : isAns ? 'rgba(5, 150, 105, 0.08)' : '#F1F5F9',
                          borderColor: isAns ? 'success.main' : 'transparent',
                          color: isCur ? '#FFF' : isAns ? 'success.main' : '#475569',
                        }}
                      >
                        {i + 1}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: '0.8rem', color: '#64748B' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: 'primary.main' }} />
                  <Typography variant="caption">ข้อที่กำลังทำอยู่</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: 'rgba(5, 150, 105, 0.2)', border: '1px solid #059669' }} />
                  <Typography variant="caption">ตอบแล้ว ({answeredCount} ข้อ)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: '#F1F5F9' }} />
                  <Typography variant="caption">ยังไม่ตอบ ({totalQuestions - answeredCount} ข้อ)</Typography>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<Send size={18} />}
                onClick={() => setConfirmSubmitOpen(true)}
                sx={{ mt: 3, borderRadius: 2.5, fontWeight: 700, py: 1.4 }}
              >
                ส่งข้อสอบและตรวจคำตอบ
              </Button>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Mobile Bottom Sticky Navigation Action Bar */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: '#FFFFFF',
            px: 2,
            py: 1.2,
            boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
            zIndex: 1100,
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #E2E8F0',
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            disabled={currentQuestionIndex === 0}
            onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
            sx={{ borderRadius: 2, minWidth: 95 }}
          >
            ข้อก่อนหน้า
          </Button>

          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            ข้อ {currentQuestionIndex + 1} / {totalQuestions}
          </Typography>

          {currentQuestionIndex < totalQuestions - 1 ? (
            <Button
              size="small"
              variant="contained"
              endIcon={<ArrowRight size={16} />}
              onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
              sx={{ borderRadius: 2, minWidth: 95 }}
            >
              ข้อถัดไป
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Send size={16} />}
              onClick={() => setConfirmSubmitOpen(true)}
              sx={{ borderRadius: 2, minWidth: 95, fontWeight: 700 }}
            >
              ส่งข้อสอบ
            </Button>
          )}
        </Box>

        {/* Confirmation Modal */}
        <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
            ยืนยันการส่งข้อสอบ
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
            <AlertTriangle size={48} color="#D97706" style={{ margin: '0 auto 12px' }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              คุณได้ตอบคำถามแล้ว <strong>{answeredCount}</strong> จากทั้งหมด <strong>{totalQuestions}</strong> ข้อ
            </Typography>
            {answeredCount < totalQuestions && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                ยังมีข้อที่คุณยังไม่ได้ตอบจำนวน <strong>{totalQuestions - answeredCount}</strong> ข้อ หากส่งตอนนี้ ข้อที่ยังไม่ตอบจะได้ 0 คะแนน
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              เมื่อส่งแล้ว คุณจะไม่สามารถกลับมาแก้ไขคำตอบได้อีก ยืนยันการส่งข้อสอบหรือไม่?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 1 }}>
            <Button onClick={() => setConfirmSubmitOpen(false)} disabled={submitting}>
              กลับไปทำต่อ
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmitExam(false)}
              disabled={submitting}
            >
              {submitting ? 'กำลังประมวลผล...' : 'ยืนยันส่งข้อสอบ'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Anti-Cheating Violation Warning Dialog (Active Session View) */}
        <Dialog
          open={Boolean(violationAlert?.open)}
          onClose={(_event, reason) => {
            if (reason === 'backdropClick') return;
            setViolationAlert(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldAlert size={22} color="#DC2626" />
            คำเตือนการผิดระเบียบการสอบ
          </DialogTitle>
          <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left', fontWeight: 600 }}>
              ระบบตรวจพบว่าท่านออกจากหน้าต่างข้อสอบ หรือสลับแท็บเบราว์เซอร์
            </Alert>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              จำนวนครั้งที่สลับหน้าจอ: <strong style={{ color: '#DC2626', fontSize: '1.2rem' }}>{violationAlert?.count}</strong> / {violationAlert?.max} ครั้ง
            </Typography>
            <Typography variant="body2" color="text.secondary">
              หากสลับหน้าจอเกินกำหนด ระบบจะทำการตัดสิทธิ์และส่งข้อสอบโดยอัตโนมัติทันที
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setViolationAlert(null)}
              sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
            >
              รับทราบและกลับไปทำข้อสอบ
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ========================================================
  // RENDER 2: AVAILABLE EXAMS OVERVIEW (หน้ารายการชุดข้อสอบ)
  // ========================================================
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
            COMPREHENSIVE EXAMINATION PORTAL
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
            การสอบประมวลผลความรู้
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            การทดสอบประมวลผลความรู้วิชาชีพครูตามรายวิชาที่ลงทะเบียนฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มรภ.กำแพงเพชร
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Grid container spacing={3}>
        {exams.map((ex) => {
          const attempt = ex.attempt;
          const isCompleted = ex.is_completed;
          const isOpen = ex.window_status === 'OPEN';
          const isPending = ex.window_status === 'PENDING';
          const isClosed = ex.window_status === 'CLOSED';

          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={ex.id}>
              <GlassCard
                highlightColor={isCompleted ? (attempt?.is_passed ? '#059669' : '#DC2626') : '#1E40AF'}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                    <Chip
                      label={ex.course_code ? `${ex.course_code} ${ex.course_name}` : 'ทุกรายวิชา'}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600, maxWidth: '75%' }}
                    />
                    {isCompleted ? (
                      attempt?.is_passed ? (
                        <Chip label="ผ่านเกณฑ์" color="success" size="small" sx={{ fontWeight: 700 }} />
                      ) : (
                        <Chip label="ไม่ผ่านเกณฑ์" color="error" size="small" sx={{ fontWeight: 700 }} />
                      )
                    ) : ex.is_in_progress || attempt?.status === 'IN_PROGRESS' ? (
                      <Chip
                        icon={<RotateCcw size={13} className="animate-spin" />}
                        label="กำลังทำค้างอยู่"
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    ) : isPending ? (
                      <Chip label="ยังไม่ถึงเวลาเปิดสอบ" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                    ) : isClosed ? (
                      <Chip label="หมดเวลาสอบแล้ว" size="small" color="default" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Chip label="เปิดให้สอบแล้ว" color="success" size="small" sx={{ fontWeight: 700 }} />
                    )}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E3A8A', mb: 1 }}>
                    {ex.title}
                  </Typography>

                  {ex.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {ex.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Grid container spacing={1} sx={{ mb: 2, fontSize: '0.85rem' }}>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                        <Clock size={16} color="#2563EB" />
                        <Typography variant="body2">
                          เวลาสอบ: <strong>{ex.duration_minutes} นาที</strong>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                        <HelpCircle size={16} color="#7C3AED" />
                        <Typography variant="body2">
                          ข้อสอบ: <strong>{ex.question_count || 0} ข้อ</strong>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                        <Award size={16} color="#059669" />
                        <Typography variant="body2">
                          เกณฑ์ผ่าน: <strong>{ex.passing_percentage}%</strong>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                        <Calendar size={16} color="#D97706" />
                        <Typography variant="body2">
                          ภาคเรียน: <strong>{ex.semester}/{ex.academic_year}</strong>
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Datetime Window Details */}
                  <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, mb: 1.5, border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      เปิด: {ex.start_datetime ? new Date(ex.start_datetime).toLocaleString('th-TH') : 'เปิดตลอด'}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      ปิด: {ex.end_datetime ? new Date(ex.end_datetime).toLocaleString('th-TH') : 'ไม่ระบุวันปิด'}
                    </Typography>
                  </Box>

                  {/* Geofence & Proctoring Policy Chips */}
                  <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 2 }}>
                    {ex.require_geofence && (
                      <Chip
                        icon={<MapPin size={13} />}
                        label={`ต้องอยู่ในพื้นที่ (รัศมี ${ex.radius_meters || 500} ม.)`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                      />
                    )}
                    <Chip
                      icon={<ShieldAlert size={13} />}
                      label={`จำกัดสลับหน้าจอ ≤ ${ex.allowed_tab_switches || 3} ครั้ง`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                    />
                  </Box>

                  {/* Completed Score Summary */}
                  {isCompleted && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: attempt?.is_passed ? 'rgba(5, 150, 105, 0.08)' : 'rgba(220, 38, 38, 0.08)',
                        border: '1px solid',
                        borderColor: attempt?.is_passed ? 'rgba(5, 150, 105, 0.3)' : 'rgba(220, 38, 38, 0.3)',
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: attempt?.is_passed ? '#059669' : '#DC2626' }}>
                        คะแนนที่ได้: {attempt?.score} / {attempt?.total_points || ex.total_points} ({Number(attempt?.percentage).toFixed(1)}%)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        สอบเมื่อ {new Date(attempt?.submitted_at).toLocaleString('th-TH')}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ pt: 1 }}>
                  {isCompleted ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={() => handleViewResult(ex.id)}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                      ดูผลคะแนนการสอบ
                    </Button>
                  ) : ex.is_in_progress || attempt?.status === 'IN_PROGRESS' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Alert severity="warning" sx={{ py: 0.5, px: 1.5, fontSize: '0.8rem', borderRadius: 2 }}>
                        คุณกำลังทำข้อสอบค้างอยู่ (ระบบได้บันทึกคำตอบไว้แล้ว)
                      </Alert>
                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        startIcon={<RotateCcw size={18} />}
                        onClick={() => handleStartExam(ex, true)}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 700,
                          py: 1.2,
                          bgcolor: '#D97706',
                          '&:hover': { bgcolor: '#B45309' },
                          boxShadow: '0 4px 14px rgba(217, 119, 6, 0.35)',
                        }}
                      >
                        ทำข้อสอบต่อ (Resume Exam)
                      </Button>
                    </Box>
                  ) : isOpen ? (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => handleStartExam(ex, false)}
                      sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}
                    >
                      เริ่มทำข้อสอบ
                    </Button>
                  ) : isPending ? (
                    <Button fullWidth variant="outlined" disabled sx={{ borderRadius: 2 }}>
                      ยังไม่ถึงเวลาเปิดสอบ
                    </Button>
                  ) : (
                    <Button fullWidth variant="outlined" disabled sx={{ borderRadius: 2 }}>
                      สิ้นสุดเวลาสอบแล้ว
                    </Button>
                  )}
                </Box>
              </GlassCard>
            </Grid>
          );
        })}

        {exams.length === 0 && !loading && (
          <Grid size={{ xs: 12 }}>
            <GlassCard sx={{ p: 5, textAlign: 'center' }}>
              <BookOpen size={44} color="#94A3B8" style={{ marginBottom: 12 }} />
              <Typography variant="h6" color="text.secondary">
                ไม่พบชุดข้อสอบประมวลผลความรู้สำหรับรายวิชาที่คุณลงทะเบียนในขณะนี้
              </Typography>
            </GlassCard>
          </Grid>
        )}
      </Grid>

      {/* Result Review Modal */}
      <Dialog
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, bgcolor: '#1E40AF', color: '#FFF' }}>
          ผลการสอบประมวลผลความรู้: {reviewResult?.attempt?.exam_title}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {reviewResult && (
            <Box>
              {/* Score Summary Box */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2.5,
                  textAlign: 'center',
                  bgcolor: reviewResult.attempt?.is_passed ? 'rgba(5, 150, 105, 0.08)' : 'rgba(220, 38, 38, 0.08)',
                  border: '1px solid',
                  borderColor: reviewResult.attempt?.is_passed ? '#059669' : '#DC2626',
                }}
              >
                <Typography variant="overline" sx={{ fontWeight: 700, color: reviewResult.attempt?.is_passed ? '#059669' : '#DC2626' }}>
                  {reviewResult.attempt?.is_passed ? 'ผ่านเกณฑ์การทดสอบ' : 'ไม่ผ่านเกณฑ์การทดสอบ'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: reviewResult.attempt?.is_passed ? '#059669' : '#DC2626', my: 1 }}>
                  {reviewResult.attempt?.score} / {reviewResult.attempt?.total_points}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  คิดเป็น <strong>{Number(reviewResult.attempt?.percentage).toFixed(1)}%</strong> (เกณฑ์ผ่าน {reviewResult.attempt?.passing_percentage}%)
                </Typography>
              </Paper>

              {/* Additional Attempt Details */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F8FAFC' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      สถานะการสอบ:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.3 }}>
                      ส่งคำตอบและบันทึกคะแนนเรียบร้อยแล้ว
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      วันที่และเวลาส่งคำตอบ:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.3 }}>
                      {reviewResult.attempt?.submitted_at
                        ? new Date(reviewResult.attempt.submitted_at).toLocaleString('th-TH')
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
                  คะแนนผลการสอบประมวลผลความรู้นี้จะถูกนำไปเชื่อมโยงและคำนวณรวมกับคะแนนประเมินภาระงานประจำรายวิชาต่อไป
                </Alert>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewModalOpen(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>

      {/* Anti-Cheating Violation Warning Dialog */}
      <Dialog
        open={Boolean(violationAlert?.open)}
        onClose={() => setViolationAlert(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldAlert size={22} color="#DC2626" />
          คำเตือนการผิดระเบียบการสอบ
        </DialogTitle>
        <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left', fontWeight: 600 }}>
            ระบบตรวจพบว่าท่านออกจากหน้าต่างข้อสอบ หรือสลับแท็บเบราว์เซอร์
          </Alert>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            จำนวนครั้งที่สลับหน้าจอ: <strong>{violationAlert?.count}</strong> / {violationAlert?.max} ครั้ง
          </Typography>
          <Typography variant="body2" color="text.secondary">
            หากสลับหน้าจอเกินกำหนด ระบบจะทำการตัดสิทธิ์และส่งข้อสอบโดยอัตโนมัติทันที
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setViolationAlert(null)}
            sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            รับทราบและกลับไปทำข้อสอบ
          </Button>
        </DialogActions>
      </Dialog>

      {/* GPS Geofence Verification Dialog */}
      <Dialog
        open={Boolean(geofenceModal?.open)}
        onClose={() => setGeofenceModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapPin size={22} color="#1E40AF" />
          ตรวจสอบพื้นที่การสอบ (GPS Geofencing)
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {geofenceModal?.message}
          </Alert>

          {geofenceModal?.exam && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1E3A8A' }}>
                แผนที่ดาวเทียมแสดงจุดศูนย์กลางและรัศมีที่อนุญาต ({geofenceModal.exam.radius_meters || 500} เมตร):
              </Typography>
              <SatelliteMapPicker
                latitude={Number(geofenceModal.exam.latitude) || 16.48278}
                longitude={Number(geofenceModal.exam.longitude) || 99.52222}
                radiusMeters={Number(geofenceModal.exam.radius_meters) || 500}
                readOnly
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                * หากท่านอยู่ที่สถานที่สอบแล้วแต่ยังไม่สามารถเข้าสอบได้ กรุณาตรวจสอบให้แน่ใจว่าได้เปิด GPS/Location บนอุปกรณ์และให้สิทธิ์การเข้าถึงตำแหน่งกับเบราว์เซอร์
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGeofenceModal(null)}>ปิดหน้าต่าง</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              const ex = geofenceModal?.exam;
              setGeofenceModal(null);
              if (ex) handleStartExam(ex);
            }}
          >
            ลองตรวจสอบพิกัดอีกครั้ง
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentExamPortal;
